import { Button, Form } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import Page from "~Common/Page";
import PermissionCheck from "~Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { formStyles } from "~pages/Fixtures/FixtureManagePage/FixtureManagePage.styles.ts";
import { usePersonService } from "~pages/Persons/hooks/usePersonService.ts";
import { PersonProfileBasicSection } from "~pages/Persons/PersonProfile/components/PersonProfileBasicSection.tsx";
import { PersonProfileFormTabs } from "~pages/Persons/PersonProfile/components/PersonProfileFormTabs/PersonProfileFormTabs.tsx";
import { useFetchPerson } from "~pages/Persons/PersonProfile/hooks/useFetchPerson.ts";
import type { UpsertPersonForm } from "~pages/Persons/PersonProfile/types/upsertPersonForm.ts";
import "./PersonProfile.scss";

const PersonProfile = () => {
  const { personId } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm<UpsertPersonForm>();

  const { data, isLoading } = useFetchPerson(personId || "");
  const { updatePerson, isUpdatePending: isPending } = usePersonService();

  const onSave = async (values: UpsertPersonForm) => {
    // biome-ignore lint/correctness/noUnusedVariables: we are excluding data not used in the request here
    const { personId, externalId, updated, added, ...updateData } = values;
    if (!personId) {
      return;
    }

    await updatePerson(personId, {
      ...updateData,
      dob: values.dob ? dayjs(values.dob).format("YYYY-MM-DD") : null,
      deceased: values.deceased ? dayjs(values.deceased).format("YYYY-MM-DD") : null,
    });
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        additionalDetails: data.additionalDetails,
        dob: data.dob ? dayjs(data.dob) : null,
        deceased: data.deceased ? dayjs(data.deceased) : null,
        gender: data.gender || "",
        nameAbbreviated: data.nameAbbreviated || null,
        nameFamilyLocal: data.nameFamilyLocal || null,
        nameFamilyLatin: data.nameFamilyLatin || null,
        nameGivenLocal: data.nameGivenLocal || null,
        nameGivenLatin: data.nameGivenLatin || null,
        nationality: data.nationality || null,
        status: data.status || "",
        nameFullLocal: data.nameFullLocal || "",
        nameFullLatin: data.nameFullLatin || null,
        personId: data.personId || "",
        externalId: data.externalId || null,
        added: data.added && dayjs(data.added).format("DD/MM/YYYY"),
        updated: data.updated && dayjs(data.updated).format("DD/MM/YYYY"),
        additionalNames: {
          default: {
            knownAs: data.additionalNames?.default?.knownAs || null,
          },
        },
        social: {
          facebook: data.social?.facebook || null,
          twitter: data.social?.twitter || null,
          instagram: data.social?.instagram || null,
          wikipedia: data.social?.wikipedia || null,
          website: data.social?.website || null,
        },
      });
    }
  }, [data, form]);

  return (
    <Page title={data?.nameFullLocal} tabTitle={data?.nameFullLocal}>
      <div className="person-profile">
        <Form<UpsertPersonForm> form={form} onFinish={onSave} style={formStyles}>
          <PersonProfileBasicSection isLoading={isLoading} form={form} data={data} />
          <PersonProfileFormTabs data={data} personId={personId} isLoading={isLoading} form={form} />
          <PermissionCheck permissionKeys={[PermissionNames.PERSON]} permissionOptions={[PermissionOperations.UPDATE]}>
            <div className="person-profile-buttons">
              <Button
                className="person-profile-update-button"
                type="primary"
                onClick={() => form.submit()}
                disabled={isLoading}
                loading={isPending}
              >
                {formatMessage({ id: "update" })}
              </Button>
            </div>
          </PermissionCheck>
        </Form>
      </div>
    </Page>
  );
};

export default PersonProfile;
